import React, {useEffect, useRef, useState} from "react";
import PropTypes from "prop-types";
import {Box, CircularProgress, Tooltip} from "@mui/material";


import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
    black500,
    greyishBlue100,
    greyishBlue500,
    greyishBlue600,
} from "assets/jss/main";

import EvolutionBadge from "components/dashboard/EvolutionBadge";

import {UserAgentUtils} from "utils/useragent";

let closeActiveTooltip = null;

function StatCard({
    label,
    value,
    evolutionLabel,
    evolutionPositive,
    segmentAverage,
    loading,
    tooltip,
    tag,
    tagBelow = false,
}) {
    const [tooltipOpen, setTooltipOpen] = useState(false);
    const myCloseRef = useRef(null);

    const isMobile = UserAgentUtils.isMobile();

    const handleTooltipOpen = () => {
        if (closeActiveTooltip) {
            closeActiveTooltip();
        }

        const close = () => setTooltipOpen(false);
        myCloseRef.current = close;
        setTooltipOpen(true);
        closeActiveTooltip = close;
    };

    const handleTooltipClose = () => {
        setTooltipOpen(false);
        if (closeActiveTooltip === myCloseRef.current) {
            closeActiveTooltip = null;
        }
    };

    useEffect(() => {
        return () => {
            if (closeActiveTooltip === myCloseRef.current) {
                closeActiveTooltip = null;
            }
        };
    }, []);

    return (
        <Box
            sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                gap: "12px",
                backgroundColor: "#fff",
                borderRadius: "16px",
                border: `1px solid ${greyishBlue100}`,
                p: isMobile ? '16px ': '20px',
                minWidth: 0,
            }}
        >
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{
                    color: greyishBlue600,
                    fontSize: isMobile ? '13px' : '16px',
                    fontWeight: isMobile ? 500 : 600
                }}>
                    {label}
                </div>
                {tooltip && (
                    <Tooltip
                        title={<span style={{ whiteSpace: "pre-line" }}>{tooltip}</span>}
                        arrow
                        placement="top"
                        open={tooltipOpen}
                        onOpen={handleTooltipOpen}
                        onClose={handleTooltipClose}
                        enterTouchDelay={0}
                        leaveTouchDelay={3000}
                    >
                        <InfoOutlinedIcon sx={{ fontSize: 20, color: greyishBlue500, cursor: "help" }} />
                    </Tooltip>
                )}
            </Box>
            {loading ? (
                <Box sx={{ py: 1 }}>
                    <CircularProgress size={20} />
                </Box>
            ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
                        <div style={{
                            fontWeight: 700,
                            color: black500,
                            fontSize: isMobile ? '28px' : '32px',
                            lineHeight: 1.2
                        }}>
                            {value}
                        </div>
                        {evolutionLabel !== undefined && (
                            <EvolutionBadge label={evolutionLabel} positive={evolutionPositive} />
                        )}
                        {tag && !tagBelow && tag}
                    </Box>
                    {tag && tagBelow && tag}
                    {segmentAverage && (
                        <div style={{
                            color: greyishBlue500,
                            fontSize: isMobile ? '11px' : '12px'
                        }}>
                            {segmentAverage}
                        </div>
                    )}
                </Box>
            )}
        </Box>
    );
}

StatCard.propTypes = {
    label: PropTypes.node.isRequired,
    value: PropTypes.node,
    evolutionLabel: PropTypes.string,
    evolutionPositive: PropTypes.bool,
    segmentAverage: PropTypes.node,
    loading: PropTypes.bool,
    tooltip: PropTypes.string,
    tag: PropTypes.node,
    tagBelow: PropTypes.bool,
};

export default StatCard;
